import { useTranslation } from 'next-i18next'
import React, { FunctionComponent } from 'react'
import { usePhoneNumber } from '@afterpaytouch/core'
import { usePostLoginRoute } from '../../config/router'
import {
  ADDRESS_1,
  ADDRESS_2,
  AREA_2,
  COUNTRY_CODE,
  FULL_CONTACT_NAME,
  IS_PREFERRED_ADDRESS,
  MOBILE,
  POSTCODE,
  ShippingAddress,
  ShippingAddressFormData,
  STATE,
  SUBURB,
} from '../../model'
import { updateShippingAddress, useAppDispatch, useAppRouter, useCountry, useShippingAddress } from '../../state'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { ShippingAddressForm } from '../ShippingAddressForm/ShippingAddressForm'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'

interface EditShippingAddressFormProps {
  id?: number | string
}

export const EditShippingAddressForm: FunctionComponent<EditShippingAddressFormProps> = ({ id }): JSX.Element => {
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { allAddresses } = useShippingAddress()
  const { t } = useTranslation()
  const country = useCountry()
  const postLoginRoute = usePostLoginRoute()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const selectedAddress = allAddresses.find((address) => address.id === id)
  const { parseMobile } = usePhoneNumber({ country: selectedAddress?.countryCode ?? country })

  const addressToEdit = (): ShippingAddress | undefined => {
    if (!isUndefinedOrEmpty(selectedAddress?.phoneNumber)) {
      parseMobile(selectedAddress?.phoneNumber)
    }
    return selectedAddress
  }

  const formattedAddress = addressToEdit()

  const initialFormValues = {
    [FULL_CONTACT_NAME]: formattedAddress?.name ?? '',
    [MOBILE]: formattedAddress?.phoneNumber ?? '',
    [COUNTRY_CODE]: formattedAddress?.countryCode ?? country,
    [ADDRESS_1]: formattedAddress?.address1 ?? '',
    [ADDRESS_2]: formattedAddress?.address2 ?? '',
    [AREA_2]: formattedAddress?.area2 ?? '',
    [POSTCODE]: formattedAddress?.postcode ?? '',
    [STATE]: formattedAddress?.state ?? '',
    [SUBURB]: formattedAddress?.suburb ?? '',
    [IS_PREFERRED_ADDRESS]: formattedAddress?.preferred ?? false,
  }

  const onSave = async (formData: ShippingAddressFormData): Promise<void> => {
    const addressDetails = {
      name: formData[FULL_CONTACT_NAME],
      countryCode: formData[COUNTRY_CODE],
      address1: formData[ADDRESS_1],
      address2: formData[ADDRESS_2],
      area2: formData[AREA_2],
      suburb: formData[SUBURB],
      postcode: formData[POSTCODE],
      state: formData[STATE],
    }

    const shippingAddress = {
      shippingAddressId: id,
      addressUpdateDetails: {
        phoneNumber: formData[MOBILE],
        shippingAddress: addressDetails,
      },
    }

    await dispatch(updateShippingAddress(shippingAddress))
    logEvent('Viewed Save Edit Shipping Address Check', {
      initialAddressCountryCode: initialFormValues[COUNTRY_CODE],
      addressCountryCode: addressDetails.countryCode,
      addressPostcode: addressDetails.postcode,
      addressState: addressDetails.state,
      checkoutCountryCode: country,
    })

    router.push(postLoginRoute)
  }

  const saveButtonLabel = t('addNewAddress:saveAddressButtonLabel')

  return (
    <ShippingAddressForm
      editMode
      addressToEdit={formattedAddress}
      onSaveHandler={onSave}
      saveButtonLabel={saveButtonLabel}
      initialFormValues={initialFormValues}
    />
  )
}
